(() => {
  const initializedAttribute = 'data-furthr-dots-ready';

  const preloadInitializedAttribute = 'data-furthr-preload-ready';

  function getRealSlides(sliderComponent) {
    return sliderComponent.sliderItemsToShow
      ? Array.from(sliderComponent.sliderItemsToShow)
      : Array.from(sliderComponent.querySelectorAll('[id^="Slide-"]')).filter(
          (slide) => slide.clientWidth > 0
        );
  }

  function preloadImage(image) {
    if (!(image instanceof HTMLImageElement) || image.dataset.furthrPreloaded === 'true') return;

    image.dataset.furthrPreloaded = 'true';
    image.loading = 'eager';
    image.decoding = 'async';

    // Changing a lazy image to eager starts the responsive srcset request.
    // decode() then warms the decoded bitmap so the next swipe can paint immediately.
    image.decode?.().catch(() => {
      // A failed decode should never interrupt carousel navigation.
    });
  }

  function preloadSlide(slide) {
    slide?.querySelectorAll('img').forEach(preloadImage);
  }

  function preloadAdjacentSlides(sliderComponent, page = 1) {
    const slides = getRealSlides(sliderComponent);
    if (!slides.length) return;

    const currentIndex = Math.min(Math.max(Number(page || 1) - 1, 0), slides.length - 1);
    const nextIndex = (currentIndex + 1) % slides.length;
    const previousIndex = (currentIndex - 1 + slides.length) % slides.length;

    // The active slide is normally loaded already, but including it also covers
    // slides selected through variant changes or dot navigation.
    preloadSlide(slides[currentIndex]);
    preloadSlide(slides[nextIndex]);
    preloadSlide(slides[previousIndex]);
  }

  function setupCarouselPreloading(scope = document) {
    scope
      .querySelectorAll('media-gallery[data-featured-product-carousel="true"] slider-component')
      .forEach((sliderComponent) => {
        if (sliderComponent.hasAttribute(preloadInitializedAttribute)) return;
        sliderComponent.setAttribute(preloadInitializedAttribute, 'true');

        const preloadCurrentNeighbors = (page) => preloadAdjacentSlides(
          sliderComponent,
          page || sliderComponent.currentPage || 1
        );

        sliderComponent.addEventListener('slideChanged', (event) => {
          preloadCurrentNeighbors(event.detail?.currentPage);
        });

        // Start loading the adjacent images as soon as the gallery is ready.
        requestAnimationFrame(() => preloadCurrentNeighbors());

        // Warm the destination slide at the earliest sign of mouse/touch intent.
        sliderComponent.querySelectorAll('button[name="previous"], button[name="next"]').forEach((button) => {
          const preloadDestination = () => {
            const slides = getRealSlides(sliderComponent);
            if (!slides.length) return;

            const currentIndex = Math.min(
              Math.max(Number(sliderComponent.currentPage || 1) - 1, 0),
              slides.length - 1
            );
            const destinationIndex = button.name === 'next'
              ? (currentIndex + 1) % slides.length
              : (currentIndex - 1 + slides.length) % slides.length;

            preloadSlide(slides[destinationIndex]);
          };

          button.addEventListener('pointerenter', preloadDestination, { passive: true });
          button.addEventListener('pointerdown', preloadDestination, { passive: true });
          button.addEventListener('touchstart', preloadDestination, { passive: true });
        });

        sliderComponent.querySelectorAll('[data-slide-index]').forEach((dot) => {
          const preloadDotTarget = () => {
            const slides = getRealSlides(sliderComponent);
            preloadSlide(slides[Number(dot.dataset.slideIndex)]);
          };

          dot.addEventListener('pointerenter', preloadDotTarget, { passive: true });
          dot.addEventListener('pointerdown', preloadDotTarget, { passive: true });
          dot.addEventListener('touchstart', preloadDotTarget, { passive: true });
        });
      });
  }

  function updateDots(sliderComponent, page) {
    const dots = sliderComponent.querySelectorAll('[data-slide-index]');
    const activeIndex = Math.max(0, Number(page || 1) - 1);

    dots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle('slider-counter__link--active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  function setupCarouselDots(scope = document) {
    scope
      .querySelectorAll('media-gallery[data-featured-product-carousel="true"] slider-component')
      .forEach((sliderComponent) => {
        if (sliderComponent.hasAttribute(initializedAttribute)) return;
        sliderComponent.setAttribute(initializedAttribute, 'true');

        const dots = sliderComponent.querySelectorAll('[data-slide-index]');
        if (!dots.length) return;

        dots.forEach((dot) => {
          dot.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            const index = Number(dot.dataset.slideIndex);
            const realSlides = sliderComponent.sliderItemsToShow
              ? Array.from(sliderComponent.sliderItemsToShow)
              : Array.from(sliderComponent.querySelectorAll('[id^="Slide-"]')).filter(
                  (slide) => slide.clientWidth > 0
                );
            const targetSlide = realSlides[index];
            if (!targetSlide) return;

            if (typeof sliderComponent.setSlidePosition === 'function') {
              sliderComponent.setSlidePosition(targetSlide.offsetLeft);
            } else {
              sliderComponent.querySelector('[id^="Slider-"]')?.scrollTo({
                left: targetSlide.offsetLeft,
                behavior: 'smooth',
              });
            }
          });
        });

        sliderComponent.addEventListener('slideChanged', (event) => {
          updateDots(sliderComponent, event.detail?.currentPage || sliderComponent.currentPage || 1);
        });

        updateDots(sliderComponent, sliderComponent.currentPage || 1);
      });
  }

  function setupSizingGuideLinks(scope = document) {
    scope.querySelectorAll('[data-sizing-guide-link]').forEach((link) => {
      if (link.dataset.sizingGuideReady === 'true') return;
      link.dataset.sizingGuideReady = 'true';

      link.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const featuredProduct = link.closest('.featured-product');
        const sliderComponent = featuredProduct?.querySelector(
          'media-gallery[data-featured-product-carousel="true"] slider-component'
        );
        if (!sliderComponent) return;

        const realSlides = Array.from(sliderComponent.querySelectorAll('[id^="Slide-"]')).filter(
          (slide) => slide.clientWidth > 0
        );
        const lastSlide = realSlides[realSlides.length - 1];
        if (!lastSlide) return;

        // Jump directly to the sizing image rather than scrolling through every
        // intermediate product image, then bring the gallery comfortably into view.
        if (typeof sliderComponent.jumpToSlidePosition === 'function') {
          sliderComponent.jumpToSlidePosition(lastSlide.offsetLeft);
        } else {
          sliderComponent.querySelector('[id^="Slider-"]')?.scrollTo({
            left: lastSlide.offsetLeft,
            behavior: 'auto',
          });
        }

        requestAnimationFrame(() => {
          sliderComponent.update?.();
          updateDots(sliderComponent, realSlides.length);
          sliderComponent.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      });
    });
  }

  function positionShareButton(featuredProduct) {
    const shareButton = featuredProduct.querySelector('share-button.share-button');
    const sizeFieldset = featuredProduct.querySelector('.product-form__input--equal-size-buttons');
    if (!shareButton || !sizeFieldset) return;

    // Keep Share in the Size row at every screen width. Appending it after the
    // size pills lets CSS push it to the far right with flexible space between.
    if (shareButton.parentNode !== sizeFieldset) {
      sizeFieldset.appendChild(shareButton);
    }

    shareButton.classList.add('featured-product__size-share');
  }

  function setupSharePlacement(scope = document) {
    const featuredProducts = [];

    if (scope instanceof Element && scope.matches('.featured-product')) {
      featuredProducts.push(scope);
    }

    scope.querySelectorAll?.('.featured-product').forEach((featuredProduct) => {
      featuredProducts.push(featuredProduct);
    });

    featuredProducts.forEach(positionShareButton);
  }

  function initialize(scope = document) {
    setupCarouselPreloading(scope);
    setupCarouselDots(scope);
    setupSizingGuideLinks(scope);
    setupSharePlacement(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
